import { Gauge, TrendingUp, Route, Clock, Activity } from 'lucide-react';
import type { JobStats } from '../../types/jobs';
import { toNum, type Numeric } from '@/utils/numeric';

interface JobStatsPanelProps {
  stats: JobStats;
}

function StatCell({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg">
      <div className="mt-0.5 text-indigo-500">{icon}</div>
      <div>
        <p className="text-xs text-neutral-500">{label}</p>
        <p className="text-sm font-semibold text-neutral-800">{value}</p>
      </div>
    </div>
  );
}

function fmt(value: Numeric | undefined, unit: string, decimals = 1): string {
  const n = toNum(value);
  if (n == null) return '—';
  return `${n.toFixed(decimals)} ${unit}`;
}

function fmtMins(mins: number | null): string {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function JobStatsPanel({ stats }: JobStatsPanelProps) {
  const idlePct = (stats.active_minutes != null && stats.idle_minutes != null)
    ? Math.round((stats.idle_minutes / (stats.active_minutes + stats.idle_minutes)) * 100)
    : null;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-indigo-600" />
        <h3 className="text-sm font-medium text-neutral-700">GPS Movement Stats</h3>
        {stats.total_waypoints > 0 && (
          <span className="ml-auto text-xs text-neutral-400">{stats.total_waypoints} points recorded</span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <StatCell
          icon={<Route className="h-4 w-4" />}
          label="Total Distance"
          value={fmt(stats.total_distance_km, 'km')}
        />
        <StatCell
          icon={<Gauge className="h-4 w-4" />}
          label="Avg Speed"
          value={fmt(stats.avg_speed_kmh, 'km/h')}
        />
        <StatCell
          icon={<TrendingUp className="h-4 w-4" />}
          label="Max Speed"
          value={fmt(stats.max_speed_kmh, 'km/h')}
        />
        <StatCell
          icon={<Clock className="h-4 w-4" />}
          label="Active Time"
          value={fmtMins(stats.active_minutes)}
        />
        <StatCell
          icon={<Clock className="h-4 w-4 text-amber-400" />}
          label={idlePct != null ? `Idle Time (${idlePct}%)` : 'Idle Time'}
          value={fmtMins(stats.idle_minutes)}
        />
      </div>
    </div>
  );
}

export default JobStatsPanel;
